// src/pages/Legal/TermsOfUse.tsx

import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const APP_NAME = "Elnydo";
const COMPANY_NAME = "Elnydo Inc.";
const COMPANY_ADDRESS =
  "111 NE 1st St, 8th Floor, PMB 89294, Miami, FL 33132, United States";

const CONTACT_EMAIL = "info@elnydo.com";
const DMCA_EMAIL = "dmca@elnydo.com";
const LEGAL_ADDRESS =
  "111 NE 1st St, 8th Floor, PMB 89294, Miami, FL 33132, United States";

const UID = "123456789";
const ISIN = "987654321";
const EFFECTIVE_DATE = "2026-05-30";

const wrap: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
  background: "#000",
  color: "#ccc",
  padding: "24px",
  boxSizing: "border-box",
  fontFamily:
    "-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol",
};

const topBtn: React.CSSProperties = {
  marginLeft: 12,
  border: "1px solid #3f3f46",
  background: "#0b0b0b",
  color: "#ccc",
  borderRadius: 8,
  padding: "2px 8px",
  fontSize: 12,
  lineHeight: 1.6,
  cursor: "pointer",
};

const container: React.CSSProperties = {
  maxWidth: 900,
  margin: "0 auto",
  lineHeight: 1.6,
  fontSize: 16,
  position: "relative",
};

const card: React.CSSProperties = {
  border: "1px solid #3f3f46",
  borderRadius: 16,
  padding: 20,
  background: "#0b0b0b",
};

const h1Style: React.CSSProperties = {
  margin: "0 0 8px",
  fontSize: 28,
  fontWeight: 700,
};

const h2Style: React.CSSProperties = {
  margin: "28px 0 12px",
  fontSize: 22,
  fontWeight: 700,
};

const h3Style: React.CSSProperties = {
  margin: "22px 0 8px",
  fontSize: 18,
  fontWeight: 700,
};

const pDim: React.CSSProperties = { color: "#d4d4d8" };
const small: React.CSSProperties = { color: "#cbd5e1", fontSize: 12 };
const link: React.CSSProperties = { color: "#ccc", textDecoration: "underline" };

const divider: React.CSSProperties = {
  border: 0,
  borderTop: "1px solid #3f3f46",
  margin: "28px 0",
};

const pillLink: React.CSSProperties = {
  display: "inline-block",
  padding: "8px 12px",
  borderRadius: 10,
  border: "1px solid #3f3f46",
  background: "#0b0b0b",
  color: "#ccc",
  textDecoration: "none",
  marginRight: 8,
  marginBottom: 8,
};

function renderMarkdownLite(s: string) {
  const bolded = s.replace(
    /\*\*(.+?)\*\*/g,
    "<strong style='color:#ccc'>$1</strong>"
  );
  const italic = bolded.replace(/\*(.+?)\*/g, "<em>$1</em>");
  return <span dangerouslySetInnerHTML={{ __html: italic }} />;
}

export default function TermsOfUse() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  const vars = {
    appName: APP_NAME,
    companyName: COMPANY_NAME,
    companyAddress: COMPANY_ADDRESS,
    contactEmail: CONTACT_EMAIL,
    dmcaEmail: DMCA_EMAIL,
    legalAddress: LEGAL_ADDRESS,
    UID,
    ISIN,
    year: new Date().getFullYear(),
    date: new Date(EFFECTIVE_DATE).toLocaleDateString(),
  };

  const handleClose = () => navigate(-1);

  const goTop = () => {
    scrollerRef.current?.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const scrollToId = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
  };

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

const navItems = [
  { id: "introduction", label: t("termsOfUse.nav.introduction", vars) },
  { id: "eligibility", label: t("termsOfUse.nav.eligibility", vars) },
  { id: "account", label: t("termsOfUse.nav.account", vars) },
  { id: "service", label: t("termsOfUse.nav.service", vars) },
  { id: "content", label: t("termsOfUse.nav.content", vars) },
  { id: "music", label: t("termsOfUse.nav.music", vars) },
  { id: "acceptableUse", label: t("termsOfUse.nav.acceptableUse", vars) },
  { id: "community", label: t("termsOfUse.nav.community", vars) },
  { id: "ip", label: t("termsOfUse.nav.ip", vars) },
  { id: "copyright", label: t("termsOfUse.nav.copyright", vars) },
  { id: "thirdParty", label: t("termsOfUse.nav.thirdParty", vars) },
  { id: "termination", label: t("termsOfUse.nav.termination", vars) },
  { id: "availability", label: t("termsOfUse.nav.availability", vars) },
  { id: "liability", label: t("termsOfUse.nav.liability", vars) },
  { id: "indemnity", label: t("termsOfUse.nav.indemnity", vars) },
  { id: "changes", label: t("termsOfUse.nav.changes", vars) },
  { id: "law", label: t("termsOfUse.nav.law", vars) },
  { id: "contact", label: t("termsOfUse.nav.contact", vars) },
];

  const eligibilityBullets = t("termsOfUse.eligibility.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const accountBullets = t("termsOfUse.account.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const serviceBullets = t("termsOfUse.service.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const contentBullets = t("termsOfUse.content.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const musicBullets = t("termsOfUse.music.bullets", {
  ...vars,
  returnObjects: true,
}) as string[];

  const acceptableUseBullets = t("termsOfUse.acceptableUse.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const communityBullets = t("termsOfUse.community.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const terminationBullets = t("termsOfUse.termination.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const liabilityBullets = t("termsOfUse.liability.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  const indemnityBullets = t("termsOfUse.indemnity.bullets", {
    ...vars,
    returnObjects: true,
  }) as string[];

  return (
    <div ref={scrollerRef} style={wrap}>
      <button
        onClick={handleClose}
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          background: "transparent",
          color: "#ccc",
          fontSize: "20px",
          border: "none",
          cursor: "pointer",
        }}
        aria-label={t("termsOfUse.meta.closeAria", vars)}
        title={t("termsOfUse.meta.closeTitle", vars)}
      >
        ✖
      </button>

      <main style={container}>
        <div id="top" />

        <header style={card}>
          <h1 style={h1Style}>{t("termsOfUse.meta.title", vars)}</h1>
          <p style={{ ...small, margin: 0 }}>
            {t("termsOfUse.meta.lastUpdated", vars)}
          </p>
        </header>

        <nav style={{ margin: "16px 0" }}>
          {navItems.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              style={pillLink}
              onClick={(e) => {
                e.preventDefault();
                scrollToId(item.id);
              }}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <section id="introduction">
          <h2 style={h2Style}>
            {t("termsOfUse.introduction.title", vars)}
            <button
              onClick={goTop}
              style={topBtn}
              aria-label={t("termsOfUse.meta.top", vars)}
            >
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p
  style={{
    fontSize: 16,
    fontWeight: 700,
    color: "#ddd",
    marginBottom: 12,
  }}
>
  {t("termsOfUse.introduction.welcome", vars)}
</p>

<p style={pDim}>
  {t("termsOfUse.introduction.body", vars)}
</p>
        </section>

        <hr style={divider} />

        <section id="eligibility">
          <h2 style={h2Style}>
            {t("termsOfUse.eligibility.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <ul style={pDim}>
            {eligibilityBullets.map((s, i) => (
              <li key={`eligibility-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="account">
          <h2 style={h2Style}>
            {t("termsOfUse.account.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.account.body", vars)}</p>
          <ul style={pDim}>
            {accountBullets.map((s, i) => (
              <li key={`account-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="service">
          <h2 style={h2Style}>
            {t("termsOfUse.service.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.service.body", vars)}</p>
          <ul style={pDim}>
            {serviceBullets.map((s, i) => (
              <li key={`service-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="content">
          <h2 style={h2Style}>
            {t("termsOfUse.content.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.content.body", vars)}</p>
          <ul style={pDim}>
            {contentBullets.map((s, i) => (
              <li key={`content-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
          <p style={pDim}>{t("termsOfUse.content.licenseEnd", vars)}</p>
        </section>

<hr style={divider} />

<section id="music">
  <h2 style={h2Style}>
    {t("termsOfUse.music.title", vars)}
    <button onClick={goTop} style={topBtn}>
      {t("termsOfUse.meta.top", vars)}
    </button>
  </h2>

  <p style={pDim}>{t("termsOfUse.music.body", vars)}</p>

  <ul style={pDim}>
    {musicBullets.map((s, i) => (
      <li key={`music-${i}`}>{renderMarkdownLite(s)}</li>
    ))}
  </ul>
</section>

        <hr style={divider} />


        <section id="acceptableUse">
          <h2 style={h2Style}>
            {t("termsOfUse.acceptableUse.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <ul style={pDim}>
            {acceptableUseBullets.map((s, i) => (
              <li key={`acceptable-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="community">
          <h2 style={h2Style}>
            {t("termsOfUse.community.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.community.body", vars)}</p>
          <ul style={pDim}>
            {communityBullets.map((s, i) => (
              <li key={`community-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="ip">
          <h2 style={h2Style}>
            {t("termsOfUse.ip.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.ip.body", vars)}</p>
        </section>

        <hr style={divider} />

        <section id="copyright">
          <h2 style={h2Style}>
            {t("termsOfUse.copyright.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.copyright.body", vars)}</p>
          <address style={{ ...pDim, whiteSpace: "pre-line" as const }}>
            {t("termsOfUse.copyright.agent", vars)}
          </address>
        </section>

        <hr style={divider} />

        <section id="thirdParty">
          <h2 style={h2Style}>
            {t("termsOfUse.thirdParty.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.thirdParty.body", vars)}</p>
        </section>

        <hr style={divider} />

        <section id="termination">
          <h2 style={h2Style}>
            {t("termsOfUse.termination.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.termination.body", vars)}</p>
          <ul style={pDim}>
            {terminationBullets.map((s, i) => (
              <li key={`termination-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
          <p style={pDim}>{t("termsOfUse.termination.effect", vars)}</p>
        </section>

        <hr style={divider} />

        <section id="availability">
          <h2 style={h2Style}>
            {t("termsOfUse.availability.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.availability.body", vars)}</p>
        </section>

        <hr style={divider} />

        <section id="liability">
          <h2 style={h2Style}>
            {t("termsOfUse.liability.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.liability.body", vars)}</p>
          <ul style={pDim}>
            {liabilityBullets.map((s, i) => (
              <li key={`liability-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="indemnity">
          <h2 style={h2Style}>
            {t("termsOfUse.indemnity.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.indemnity.body", vars)}</p>
          <ul style={pDim}>
            {indemnityBullets.map((s, i) => (
              <li key={`indemnity-${i}`}>{renderMarkdownLite(s)}</li>
            ))}
          </ul>
        </section>

        <hr style={divider} />

        <section id="changes">
          <h2 style={h2Style}>
            {t("termsOfUse.changes.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.changes.body", vars)}</p>
        </section>

        <hr style={divider} />

        <section id="law">
          <h2 style={h2Style}>
            {t("termsOfUse.law.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>
          <p style={pDim}>{t("termsOfUse.law.body", vars)}</p>
        </section>

        <hr style={divider} />

        <section id="contact">
          <h2 style={h2Style}>
            {t("termsOfUse.contact.title", vars)}
            <button onClick={goTop} style={topBtn}>
              {t("termsOfUse.meta.top", vars)}
            </button>
          </h2>

          <address style={pDim}>
            {COMPANY_NAME}
            <br />
            {COMPANY_ADDRESS}
            <br />
            <a href={`mailto:${CONTACT_EMAIL}`} style={link}>
              {CONTACT_EMAIL}
            </a>
          </address>
        </section>

        <div style={{ marginTop: 24, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            onClick={() => window.print()}
            style={{ ...pillLink, textDecoration: "none", cursor: "pointer" }}
          >
            {t("termsOfUse.meta.print", vars)}
          </button>

          <a href={`mailto:${CONTACT_EMAIL}`} style={pillLink}>
            {t("termsOfUse.meta.contact", vars)}
          </a>
        </div>

        <hr style={divider} />

        <p style={{ ...small, textAlign: "left" }}>
          {t("termsOfUse.meta.copyright", vars)}
        </p>
      </main>
    </div>
  );
}